const METRIX_APP_ID = process.env.NEXT_PUBLIC_METRIX_APP_ID || "";
const METRIX_API_KEY = process.env.NEXT_PUBLIC_METRIX_API_KEY || "";
const METRIX_CONFIG = {};
const MAX_ATTRIBUTE_LENGTH = 500;
const isProduction = process.env.NODE_ENV === "production";

let metrixModulePromise = null;
let metrixInitPromise = null;
let authorizedMetrixUserId = null;
let authorizedMetrixUserSignature = null;

const isBlank = (value) => value === undefined || value === null || value === "";

const toStringValue = (value) => {
  if (isBlank(value)) return "";
  return String(value).trim();
};

const isPlainObject = (value) => {
  return Object.prototype.toString.call(value) === "[object Object]";
};

const normalizeAttributeValue = (value) => {
  if (isBlank(value)) return undefined;

  if (value instanceof Date) return value.toISOString();

  if (Array.isArray(value) || isPlainObject(value)) {
    try {
      return JSON.stringify(value).slice(0, MAX_ATTRIBUTE_LENGTH);
    } catch {
      return undefined;
    }
  }

  return String(value).slice(0, MAX_ATTRIBUTE_LENGTH);
};

export const sanitizeMetrixAttributes = (attributes = {}) => {
  return Object.entries(attributes).reduce((acc, [key, value]) => {
    const safeKey = toStringValue(key);
    const safeValue = normalizeAttributeValue(value);
    if (safeKey && safeValue !== undefined) acc[safeKey] = safeValue;
    return acc;
  }, {});
};

const callWithValue = (fn, value) => {
  const stringValue = toStringValue(value);
  if (stringValue && typeof fn === "function") fn(stringValue);
};

const getActiveProfile = (user, profileId) => {
  if (!user || !Array.isArray(user.profiles)) return null;
  return user.profiles.find((profile) => profile?._id === profileId) || null;
};

const getMetrixUserDetails = (user, profileId) => {
  const activeProfile = getActiveProfile(user, profileId);
  const phoneNumber = user?.phoneNumber || user?.mobileNumber;

  return {
    phoneNumber,
    firstName: user?.firstName,
    lastName: user?.lastName,
    email: user?.email,
    country: user?.country,
    city: user?.city,
    region: user?.state || user?.region,
    locality: user?.locality,
    gender: user?.gender,
    birthday: user?.birthday,
    profileId,
    profileFullName: activeProfile?.fullName || user?.fullName,
    profileAgeRange: activeProfile?.ageRange || user?.ageRange,
  };
};

const applyMetrixUserDetails = (metrix, details) => {
  callWithValue(metrix.setPhoneNumber, details.phoneNumber);
  callWithValue(metrix.setFirstName, details.firstName);
  callWithValue(metrix.setLastName, details.lastName);
  callWithValue(metrix.setEmail, details.email);
  callWithValue(metrix.setCountry, details.country);
  callWithValue(metrix.setCity, details.city);
  callWithValue(metrix.setRegion, details.region);
  callWithValue(metrix.setLocality, details.locality);
  callWithValue(metrix.setGender, details.gender);
  callWithValue(metrix.setBirthday, details.birthday);
  callWithValue((value) => metrix.setCustomAttribute("profile_id", value), details.profileId);
  callWithValue(
    (value) => metrix.setCustomAttribute("profile_full_name", value),
    details.profileFullName,
  );
  callWithValue(
    (value) => metrix.setCustomAttribute("profile_age_range", value),
    details.profileAgeRange,
  );
};

const getMetrixModule = () => {
  if (!isProduction || typeof window === "undefined") {
    return Promise.resolve(null);
  }

  if (!METRIX_APP_ID || !METRIX_API_KEY) {
    return Promise.resolve(null);
  }

  if (!metrixModulePromise) {
    metrixModulePromise = import("@metrixorg/websdk").catch(() => {
      metrixModulePromise = null;
      return null;
    });
  }

  return metrixModulePromise;
};

const getInitializedMetrix = async () => {
  const metrix = await getMetrixModule();
  if (!metrix) return null;

  if (!metrixInitPromise) {
    metrixInitPromise = Promise.resolve()
      .then(() => {
        metrix.init(METRIX_APP_ID, METRIX_API_KEY, METRIX_CONFIG);
        return metrix;
      })
      .catch(() => {
        metrixInitPromise = null;
        return null;
      });
  }

  return metrixInitPromise;
};

export const buildMetrixCustomUserId = (phoneNumber, profileId) => {
  const safePhoneNumber = toStringValue(phoneNumber);
  const safeProfileId = toStringValue(profileId);
  if (!safePhoneNumber || !safeProfileId) return "";
  return `${safePhoneNumber}_${safeProfileId}`;
};

export const initMetrix = async () => Boolean(await getInitializedMetrix());

export const trackMetrixEvent = async (slug, customAttributes = {}, onsuccess) => {
  const safeSlug = toStringValue(slug);
  if (!safeSlug) return false;

  const metrix = await getInitializedMetrix();
  if (!metrix) return false;

  try {
    metrix.newEvent(
      safeSlug,
      sanitizeMetrixAttributes(customAttributes),
      typeof onsuccess === "function" ? onsuccess : undefined,
    );
    return true;
  } catch {
    return false;
  }
};

export const trackMetrixEventByName = async (name, customAttributes = {}, onsuccess) => {
  const safeName = toStringValue(name);
  if (!safeName) return false;

  const metrix = await getInitializedMetrix();
  if (!metrix) return false;

  try {
    metrix.newEventByName(
      safeName,
      sanitizeMetrixAttributes(customAttributes),
      typeof onsuccess === "function" ? onsuccess : undefined,
    );
    return true;
  } catch {
    return false;
  }
};

export const trackMetrixRevenue = async (
  slug,
  revenue,
  currency = "IRR",
  onsuccess,
) => {
  const safeSlug = toStringValue(slug);
  const safeRevenue = Number(revenue);
  const currencyValue = toStringValue(currency).toUpperCase();
  const safeCurrency = ["IRR", "USD", "EUR"].includes(currencyValue)
    ? currencyValue
    : "IRR";

  if (!safeSlug || !Number.isFinite(safeRevenue)) return false;

  const metrix = await getInitializedMetrix();
  if (!metrix) return false;

  try {
    metrix.newRevenue(
      safeSlug,
      safeRevenue,
      safeCurrency,
      typeof onsuccess === "function" ? onsuccess : undefined,
    );
    return true;
  } catch {
    return false;
  }
};

export const authorizeMetrixUser = async (user, profileId) => {
  const details = getMetrixUserDetails(user, profileId);
  const customUserId = buildMetrixCustomUserId(details.phoneNumber, profileId);
  if (!customUserId) return false;

  const metrix = await getInitializedMetrix();
  if (!metrix) return false;

  try {
    const signature = JSON.stringify(details);

    if (authorizedMetrixUserId !== customUserId) {
      metrix.authorizeUser(customUserId);
      authorizedMetrixUserId = customUserId;
      authorizedMetrixUserSignature = null;
    }

    if (authorizedMetrixUserSignature !== signature) {
      applyMetrixUserDetails(metrix, details);
      authorizedMetrixUserSignature = signature;
    }

    return true;
  } catch {
    return false;
  }
};

export const deauthorizeMetrixUser = async () => {
  const metrix = await getInitializedMetrix();
  if (!metrix) return false;

  try {
    metrix.deauthorizeUser();
    authorizedMetrixUserId = null;
    authorizedMetrixUserSignature = null;
    return true;
  } catch {
    return false;
  }
};
